/**
 * ProjectFilter plugin.
 *
 * Filters grant calls by operator project IDs.
 * URL param: projectId (singular), API/SavedView: projectIds (plural).
 *
 * Fetches project list via React Query when rendered.
 *
 * Faceted counts (AC-6): when `facets.projectCounts` is provided (articles
 * page), each option shows the count of matching results. When absent (calls
 * page, which does not supply project facets), options render without counts —
 * additive and graceful, identical to the previous behaviour.
 *
 * @see docs/specifications/2026-03-10-plugin-filter-architecture.md (Section 4)
 */

import React, { useMemo } from "react";
import { FilterPlugin, FilterComponentProps } from "../types";
import { SavedViewFilters, ArticleFacetCount } from "../../../../../services/grantsApi";
import MultiSelectDropdown from "../../../../common/MultiSelectDropdown";
import { useQuery } from "@tanstack/react-query";
import apiClient from "../../../../../services/api";

/**
 * Project filter React component.
 * Fetches projects from API and renders a multi-select dropdown.
 */
const ProjectFilterComponent = React.memo(function ProjectFilterComponent(
  props: FilterComponentProps<string[]>
) {
  const { value, onChange, facets } = props;

  const { data: projectsData } = useQuery({
    queryKey: ["grants-projects"],
    queryFn: async () => {
      const { data } = await apiClient.get("/grants/projects");
      return data;
    },
  });

  const projectCounts = facets?.projectCounts as
    | ArticleFacetCount[]
    | undefined;

  const options = useMemo(
    () =>
      ((projectsData?.projects as Array<{ id: string; name: string; competition?: { program?: { code?: string } } }>) ?? []).map(
        (proj) => ({
          value: proj.id,
          label:
            proj.name +
            (proj.competition?.program?.code
              ? ` (${proj.competition.program.code})`
              : ""),
          // Faceted count (articles page): when facets are supplied, every
          // option shows a count — 0 for projects with no matches under the
          // current filters (faceted-search convention: surface alternatives).
          // When facets are absent (calls page), count stays undefined and
          // MultiSelectDropdown renders no "(N)" — identical to prior behaviour.
          count: projectCounts
            ? (projectCounts.find((c) => c.id === proj.id)?.count ?? 0)
            : undefined,
        })
      ),
    [projectsData?.projects, projectCounts]
  );

  return React.createElement("div", null,
    React.createElement("label", {
      className: "block text-sm font-medium text-neutral-700 mb-1",
    }, "Projekt operatora"),
    React.createElement(MultiSelectDropdown, {
      options,
      selectedValues: value,
      onChange,
      placeholder: "Wszystkie projekty operatorów",
      ariaLabel: "Filtruj według projektu operatora",
    }),
    React.createElement("p", {
      className: "mt-1 text-xs text-neutral-500",
    }, "Filtruj nabory według projektu operatora")
  );
});

export const ProjectFilterPlugin: FilterPlugin<string[]> = {
  key: "project",
  label: "Projekt operatora",
  placement: "advanced",
  order: 2,
  defaultValue: [],
  Component: ProjectFilterComponent,

  isActive: (value: string[]): boolean => value.length > 0,

  toUrlParams: (value: string[], params: URLSearchParams): void => {
    value.forEach((id) => params.append("projectId", id));
  },

  fromUrlParams: (params: URLSearchParams): string[] => {
    return params.getAll("projectId");
  },

  toSavedView: (
    value: string[],
    filters: Partial<SavedViewFilters>
  ): void => {
    filters.projectIds = value;
  },

  fromSavedView: (filters: SavedViewFilters): string[] => {
    return filters.projectIds ?? [];
  },

  toApiParams: (value: string[], params: URLSearchParams): void => {
    value.forEach((id) => params.append("projectIds", id));
  },
};
